import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { decodeToBase64String } from 'tools/StringTool';
import { getQueryParams } from 'tools/WebTool';
import { requestApi } from 'utils/request';
import Layout from 'layout';
import { GUIDE_TEXT_BY_MALL_OPEN_YN, makeFailUserMessage } from '../constants';
import {
  DEFAULT_MALL_DATA,
  DEFAULT_MALL_LIST_DATA,
  DEFAULT_STIPULATION_LIST,
  INIT_FORM_DATA,
  INIT_SELECTED_MANAGER_DATA,
} from './constants';
import FillAccountFormStep from './FillAccountFormStep';
import FillContactFormStep from './FillContactFormStep';
import FillCaptchaFormStep from './FillCaptchaFormStep';
import SelectMallStep from './SelectMallStep';
import SelectManagerStep from './SelectManagerStep';
import CompletionGuideStep from './CompletionGuideStep';
import CommonModal from 'components/Modal/CommonModal';
import ROUTE_PATH from 'routes/config';
import { COMMON_ERR_MESSAGE } from '../../../constants/message';
import { useUI } from 'contexts/UIContext';
import CustomerFooter from 'components/CustomerFooter';
import FillQRFormStep from './FillQRFormStep';

// 판매몰 개별 연동을 통한 데이터 연동
const DataLinkageFormPage = () => {
  const { setContextLoading } = useUI();
  const navigate = useNavigate();
  const location = useLocation();
  // url 파라미터
  // mode: 등록(register), 수정(modify)
  // accountType: 주계정(main), 부계정(sub)
  // seq: businessDataProviderMallMappingSeq
  const {
    mode = 'register',
    seq,
    mallSeq,
    type: accountType = 'main',
  } = getQueryParams(location.search);
  // 진행 단계: 판매몰 선택(mallStep), 판매몰 계정 입력(accountStep), 2차인증 수단 입력(contactStep),
  // 캡차 입력(captchaStep), 담당자 선택(managerStep), 연동 완료 안내(completionStep)
  const [step, setStep] = useState(
    location?.state?.step ||
      (mode === 'modify' || accountType === 'sub' ? 'accountStep' : 'mallStep')
  );
  const [dataProviderSeq, setDataProviderSeq] = useState(0);
  const [dataProviderIndividualList, setDataProviderIndividualList] = useState(
    []
  );
  const [mallList, setMallList] = useState(DEFAULT_MALL_LIST_DATA);
  const [formData, setFormData] = useState(INIT_FORM_DATA);
  // 캡차 데이터: 판매몰 계정 연동 시 랜덤으로 필요
  const [captchaData, setCaptchaData] = useState({});
  // 판매몰 api 데이터
  const [selectedMallData, setSelectedMallData] = useState(DEFAULT_MALL_DATA);
  // 담당자 선택
  const [selectedManager, setSelectedManager] = useState(
    INIT_SELECTED_MANAGER_DATA
  );
  // 약관 api 데이터
  const [stipulationListForMall, setStipulationListForMall] = useState(
    DEFAULT_STIPULATION_LIST
  );
  // 연동 완료 후 추가 연동 가능 판매몰이 있는지 여부
  const [hasOtherMallAccount, setHasOtherMallAccount] = useState(false);
  // 판매몰 계정 연동 시도 횟수
  const [tryCount, setTryCount] = useState(0);
  // 모달
  const [modalData, setModalData] = useState({});
  const [showModal, setShowModal] = useState(false);
  // form Key 리스트
  const formKeyList = selectedMallData.itemList?.map(item => {
    if (item?.itemId === 'id') {
      return 'mallId';
    } else if (item?.itemId === 'password') {
      return 'mallPassword';
    }
    return item?.itemId;
  });

  // 몰 오픈 여부에 따른 안내문구
  const guideTexts =
    GUIDE_TEXT_BY_MALL_OPEN_YN[selectedMallData?.interlockOpenYn];

  // 진행 단계, 데이터 초기화
  const resetAllData = event => {
    setFormData(INIT_FORM_DATA);
    setSelectedManager(INIT_SELECTED_MANAGER_DATA);
    setSelectedMallData(DEFAULT_MALL_DATA);
    setStipulationListForMall(DEFAULT_STIPULATION_LIST);
    setCaptchaData({});
    setTryCount(0);
    setStep('mallStep');
  };

  // 판매몰 인증 프로세스 실행
  const runAccountAuthProcess = () => {
    // 2회 이상 틀렸을 경우(캡차 발생은 횟수 제외): 모달창 띄우기
    if (tryCount === 2) {
      setModalData({
        contents: `계정을 2회 이상 틀리셨습니다.\n계속 틀릴 경우 아이디가 잠금 처리됩니다.\n해당 판매몰에서 아이디/비밀번호 찾기를 진행 후 계정 확인하는 것을 권장 드립니다.\n계속 진행하시겠습니까?`,
        onClickConfirmBtn: () => {
          (async () => {
            await fetchAccountAuth();
          })();
          setShowModal(false);
          // 확인 후엔 모달 뜨지 않음
          setTryCount(999);
        },
        cancelBtnName: '아니요, 계정을 확인해 볼게요',
        confirmBtnName: '네, 계속 진행할게요',
        isVerticalBtn: true,
      });
      setShowModal(true);
    } else {
      (async () => {
        await fetchAccountAuth();
      })();
    }
  };

  // 판매몰 계정 유효성 인증 api
  const fetchAccountAuth = async () => {
    let postData = {};

    // 기본 form 데이터 추가
    formKeyList?.forEach(key => {
      if (key.includes('Id') || key.includes('Password')) {
        // 방어코드: 공백 제거
        postData[key] = formData[key].replace(/\s+/g, '');
      } else {
        postData[key] = formData[key];
      }
    });

    // 캡차 인증이 있으면 인자 추가
    if (!!captchaData.captchaId) {
      postData.captchaId = captchaData.captchaId;
      postData.captchaValue = captchaData.captchaValue;
    }

    // 기타 데이터 추가
    postData = {
      ...postData,
      dataProviderSeq,
      mallSeq: selectedMallData.mallSeq,
      secondAuthTypeCd: formData.secondAuthType,
      secondAuthValue: formData.secondAuthValue,
      mallScrapingTypeCd: selectedMallData.mallScrapingTypeCd,
      secondAuthUseYn: selectedMallData.secondAuthUseYn,
      mallScreenType: mode === 'register' ? 'REG' : 'MOD',
    };

    try {
      const res = await requestApi({
        url: '/v1/au/data-provider-mall/interlock/mall-auth-validate',
        method: 'POST',
        data: postData,
      });
      // 인증 성공
      if (res.success) {
        if (selectedMallData.secondAuthCategoryCd === 'SAM200') {
          // QR 인증 발생
          if (res.data.secondAuthTypeCd === 'SAT132') {
            setStep('QRStep');
          } else {
            // QR 인증 발생하지 않음 (SAT9999)
            setStep('managerStep');
          }
          setFormData({
            ...formData,
            secondAuthType: res.data.secondAuthTypeCd,
          });
        } else {
          // 그 외 케이스
          setStep('managerStep');
        }
      } else {
        // 인증 실패: makeFailUserMessage() 에러 메시지 참고
        const { errorCode = '' } = res.data;

        const errMessage = errorCode
          ? makeFailUserMessage(errorCode, 'scraping') ||
            makeFailUserMessage('etc', 'scraping')
          : makeFailUserMessage('etc', 'scraping');

        switch (errorCode) {
          // 캡차 인증 발생: 인증 실패 케이스 아님
          case 'E11415':
            setCaptchaData({ ...res.data, captchaValue: '' });
            setStep('captchaStep');
            break;
          case 'E11611':
          case 'E11900':
            setTryCount(prev => prev + 1);
            setModalData({
              contents: errMessage,
            });
            setShowModal(true);
            break;
          default:
            setTryCount(prev => prev + 1);
            setShowModal(true);
            setModalData({
              contents: errMessage,
              confirmBtnName: '1:1 문의하기',
              cancelBtnName: '닫기',
              onClickCancelBtn: () => {
                setStep('accountStep');
                setShowModal(false);
              },
              onClickConfirmBtn: () => {
                navigate(ROUTE_PATH.serviceCenter.auth.create);
              },
            });
        }
      }
    } catch (error) {
      setShowModal(true);
      setModalData({
        contents: COMMON_ERR_MESSAGE,
      });
      console.error('error', error);
    }
  };

  // 연동 상세 데이터 조회 api
  const fetchMallInterlockDataDetail = async () => {
    try {
      const res = await requestApi({
        url: 'v1/au/data-provider-mall/interlocked-mall-detail',
        method: 'GET',
        params: { businessDataProviderMallMappingSeq: seq },
      });
      if (res.success) {
        const {
          interlockBusinessManagerName,
          interlockBusinessManagerMobilePhoneNumber,
          mainMallExtraFactor,
          subMallExtraFactor,
          mainMallId,
          mainMallPassword,
          mainMallSecondAuthTypeCd,
          mainMallSecondAuthValue,
          subMallId,
          subMallPassword,
          subMallSecondAuthTypeCd,
          subMallSecondAuthValue,
          scrapingHasSucceededYn,
          mainQrFileUrl,
          mainQrFileSeq,
          subQrFileUrl,
          subQrFileSeq,
        } = res.data.result.interlockedMallDetail;

        // 데이터 셋팅
        setSelectedManager({
          interlockBusinessManagerName,
          interlockBusinessManagerMobilePhoneNumber,
        });
        setFormData({
          ...formData,
          extraAuthFactor:
            accountType === 'main' ? mainMallExtraFactor : subMallExtraFactor,
          mallId: accountType === 'main' ? mainMallId : subMallId,
          mallPassword:
            accountType === 'main'
              ? decodeToBase64String(mainMallPassword)
              : decodeToBase64String(subMallPassword),
          secondAuthType:
            accountType === 'main'
              ? mainMallSecondAuthTypeCd
              : subMallSecondAuthTypeCd,
          secondAuthValue:
            accountType === 'main'
              ? mainMallSecondAuthValue
              : subMallSecondAuthValue,
          scrapingHasSucceededYn: scrapingHasSucceededYn,
          qrFileSeq: accountType === 'main' ? mainQrFileSeq : subQrFileSeq,
          qrFileUrl: accountType === 'main' ? mainQrFileUrl : subQrFileUrl,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  // 판매몰 개별 연동 seq 조회 api
  const fetchDataProviderSeq = async () => {
    try {
      const res = await requestApi({
        url: '/v1/au/data-provider/list/individual',
        method: 'GET',
      });
      if (res.success) {
        setDataProviderIndividualList(res.data.list);
        setDataProviderSeq(res.data.list[0]?.dataProviderSeq);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // 연동 가능 판매몰 리스트 조회 api
  const fetchMallList = async () => {
    try {
      const res = await requestApi({
        url: '/v1/au/salesMall/individual/interlock/mall',
        method: 'GET',
        params: {
          dataProviderSeq: dataProviderIndividualList[0]?.dataProviderSeq,
        },
      });
      if (res.success) {
        setMallList(res.data.result);
        // 판매몰이 이미 선택되어야 하는 경우: 목록에서 찾아서 셋팅
        if (
          !!mallSeq &&
          (mode === 'modify' || (mode === 'register' && accountType === 'sub'))
        ) {
          const flatMallList = [
            ...res.data.result?.openMarketList,
            ...res.data.result?.generalMallList,
            ...res.data.result?.homeShoppingMallList,
            ...res.data.result?.specialtyMallList,
            ...res.data.result?.paymentGateWayMallList,
            ...res.data.result?.corporateClosedShoppingMallList,
            ...res.data.result?.notOpenMallList,
          ];
          setSelectedMallData({
            ...flatMallList?.find(item => item.mallSeq === Number(mallSeq)),
          });
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setContextLoading(false);
    }
  };

  // 스크롤 위치 초기화
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // 판매몰 개별 연동 seq 조회
  useEffect(() => {
    (async () => {
      await fetchDataProviderSeq();
    })();
  }, []);

  // 연동 가능 판매몰 리스트 조회
  useEffect(() => {
    if (!(dataProviderIndividualList?.length > 0)) {
      return;
    }
    (async () => {
      await fetchMallList();
    })();
  }, [dataProviderIndividualList, mallSeq, mode, accountType]);

  // 수정 모드로 진입 시, 기존 데이터 조회 및 셋팅
  useEffect(() => {
    if (mode !== 'modify' || !seq) {
      return;
    }
    (async () => {
      await fetchMallInterlockDataDetail();
    })();
  }, [mode, seq]);

  return (
    <Layout pageTitle="데이터 연동" isShowBannerPage={false}>
      <div
        style={{
          minHeight: 'calc(100vh - 60px)',
          backgroundColor: '#f7f7f7',
        }}
      >
        <div
          style={{
            backgroundColor: '#ffffff',
          }}
        >
          {/* 판매몰 선택 */}
          {step === 'mallStep' && (
            <SelectMallStep
              setSelectedMallData={setSelectedMallData}
              setStep={setStep}
              mallList={mallList}
            />
          )}
          {/* 판매몰 계정 정보 */}
          {step === 'accountStep' && (
            <FillAccountFormStep
              mode={mode}
              formData={formData}
              selectedMallData={selectedMallData}
              setStipulationListForMall={setStipulationListForMall}
              stipulationListForMall={stipulationListForMall}
              dataProviderSeq={dataProviderSeq}
              setFormData={setFormData}
              setStep={setStep}
              resetAllData={resetAllData}
              accountType={accountType}
              runAccountAuthProcess={runAccountAuthProcess}
            />
          )}
          {/* 2차인증 수단 (해당 시) */}
          {step === 'contactStep' && (
            <FillContactFormStep
              formData={formData}
              selectedMallData={selectedMallData}
              setStep={setStep}
              setFormData={setFormData}
              setModalData={setModalData}
              setShowModal={setShowModal}
              runAccountAuthProcess={runAccountAuthProcess}
            />
          )}
          {/* 캡차 인증 발생 (해당 시) */}
          {step === 'captchaStep' && (
            <FillCaptchaFormStep
              mode={mode}
              setStep={setStep}
              captchaData={captchaData}
              setCaptchaData={setCaptchaData}
              selectedMallData={selectedMallData}
              runAccountAuthProcess={runAccountAuthProcess}
            />
          )}
          {/* QR 인증 발생 (해당 시) */}
          {step === 'QRStep' && (
            <FillQRFormStep
              mode={mode}
              selectedMallData={selectedMallData}
              setStep={setStep}
              setModalData={setModalData}
              setShowModal={setShowModal}
              formData={formData}
              setFormData={setFormData}
              setCaptchaData={setCaptchaData}
              dataProviderSeq={dataProviderSeq}
            />
          )}
          {/* 담당자 선택 */}
          {step === 'managerStep' && (
            <SelectManagerStep
              mode={mode}
              accountType={accountType}
              formData={formData}
              setFormData={setFormData}
              selectedMallData={selectedMallData}
              selectedManager={selectedManager}
              setSelectedManager={setSelectedManager}
              dataProviderSeq={dataProviderSeq}
              setHasOtherMallAccount={setHasOtherMallAccount}
              setStep={setStep}
              businessDataProviderMallMappingSeq={seq}
              stipulationListForMall={stipulationListForMall}
              setCaptchaData={setCaptchaData}
            />
          )}
          {/* 연동 완료 안내 */}
          {step === 'completionStep' && (
            <CompletionGuideStep
              hasOtherMallAccount={hasOtherMallAccount}
              dataProviderSeq={dataProviderSeq}
              selectedMallData={selectedMallData}
              completionGuideText1={guideTexts?.completionGuideText[0]}
              completionGuideText2={guideTexts?.completionGuideText[1]}
              resetAllData={resetAllData}
              mode={mode}
              accountType={accountType}
            />
          )}
          {/* 에러메세지 모달 */}
          <CommonModal
            modalData={modalData}
            setShowModal={setShowModal}
            showModal={showModal}
          />
        </div>
        {step !== 'mallStep' && <CustomerFooter />}
      </div>
    </Layout>
  );
};

export default DataLinkageFormPage;
